import { IconComponent, type IconeNome, type IconeTamanho } from '@/shared/components/ui/icon';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  booleanAttribute,
  computed,
  inject,
  input,
} from '@angular/core';

export type ButtonCor = 'primary' | 'neutral' | 'success' | 'danger' | 'highlight';
export type ButtonVariante = 'solid' | 'outline' | 'ghost';
export type ButtonTamanho = 'sm' | 'md' | 'lg';
export type ButtonEstado = 'normal' | 'carregando' | 'desabilitado';

// Sem `border`/`p-*`/`gap-*`/`bg-transparent` do Bootstrap (têm !important): borda via `border-solid border-[1px]`,
// fundo transparente via `bg-[transparent]` (só nas variantes sem fundo) e espaçamento em valores arbitrários. O `hover:text-*` repete a cor para vencer o `a:hover` do Bootstrap.
// Peso da fonte por variante: `font-semibold` no solid, `font-medium` no outline/ghost.
// `border-transparent` só no ghost: no CSS gerado ela vem depois das cores `rfy` e anularia a borda do outline.
const CLASSES_COR_VARIANTE: Record<ButtonCor, Record<ButtonVariante, string>> = {
  primary: {
    solid:
      'font-semibold bg-rfy-primary border-rfy-primary text-rfy-primary-contrast hover:text-rfy-primary-contrast ' +
      'hover:bg-rfy-primary/90 active:bg-rfy-primary/80',
    outline: 'font-medium bg-[transparent] border-rfy-primary text-rfy-primary hover:text-rfy-primary hover:bg-rfy-primary/10 active:bg-rfy-primary/20',
    ghost: 'font-medium bg-[transparent] border-transparent text-rfy-primary hover:text-rfy-primary hover:bg-rfy-primary/10 active:bg-rfy-primary/20',
  },
  neutral: {
    solid:
      'font-semibold bg-rfy-subtle border-rfy-border text-rfy-foreground hover:text-rfy-foreground ' +
      'hover:bg-rfy-border/60 active:bg-rfy-border',
    outline: 'font-medium bg-[transparent] border-rfy-border-strong text-rfy-foreground hover:text-rfy-foreground hover:bg-rfy-subtle active:bg-rfy-subtle',
    ghost: 'font-medium bg-[transparent] border-transparent text-rfy-subdued hover:text-rfy-foreground-strong hover:bg-rfy-subtle active:bg-rfy-subtle',
  },
  success: {
    solid:
      'font-semibold bg-rfy-success border-rfy-success text-rfy-success-contrast hover:text-rfy-success-contrast ' +
      'hover:bg-rfy-success/90 active:bg-rfy-success/80',
    outline: 'font-medium bg-[transparent] border-rfy-success text-rfy-success hover:text-rfy-success hover:bg-rfy-success/10 active:bg-rfy-success/20',
    ghost: 'font-medium bg-[transparent] border-transparent text-rfy-success hover:text-rfy-success hover:bg-rfy-success/10 active:bg-rfy-success/20',
  },
  danger: {
    solid:
      'font-semibold bg-rfy-danger border-rfy-danger text-rfy-danger-contrast hover:text-rfy-danger-contrast ' +
      'hover:bg-rfy-danger/90 active:bg-rfy-danger/80',
    outline: 'font-medium bg-[transparent] border-rfy-danger text-rfy-danger hover:text-rfy-danger hover:bg-rfy-danger/10 active:bg-rfy-danger/20',
    ghost: 'font-medium bg-[transparent] border-transparent text-rfy-danger hover:text-rfy-danger hover:bg-rfy-danger/10 active:bg-rfy-danger/20',
  },
  highlight: {
    solid:
      'font-semibold bg-rfy-highlight border-rfy-highlight text-rfy-highlight-contrast hover:text-rfy-highlight-contrast ' +
      'hover:bg-rfy-highlight/90 active:bg-rfy-highlight/80',
    outline: 'font-medium bg-[transparent] border-rfy-highlight text-rfy-highlight hover:text-rfy-highlight hover:bg-rfy-highlight/10 active:bg-rfy-highlight/20',
    ghost: 'font-medium bg-[transparent] border-transparent text-rfy-highlight hover:text-rfy-highlight hover:bg-rfy-subtle active:bg-rfy-subtle',
  },
};

const CLASSES_TAMANHO: Record<ButtonTamanho, string> = {
  sm: 'h-7 px-[0.5rem] text-xs',
  md: 'h-[2.125rem] px-[0.75rem] text-[0.8125rem]',
  lg: 'h-11 px-[1rem] text-sm',
};

// Botão só com ícone (sem texto): quadrado.
const CLASSES_QUADRADO: Record<ButtonTamanho, string> = {
  sm: 'size-7 px-[0]',
  md: 'size-[2.125rem] px-[0]',
  lg: 'size-11 px-[0]',
};

// Ícone acompanha o botão: menor no `sm`, `md` no `lg`.
const ICONE_TAMANHO: Record<ButtonTamanho, IconeTamanho> = { sm: 'xs', md: 'sm', lg: 'md' };

const CLASSES_BASE =
  'relative inline-flex shrink-0 items-center justify-center rounded-controle border-solid border-[1px] ' +
  'leading-none no-underline hover:no-underline whitespace-nowrap select-none cursor-pointer ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rfy-primary/50 ' +
  'focus-visible:ring-offset-1 focus-visible:ring-offset-rfy-paper ' +
  'data-[estado=desabilitado]:opacity-50 data-[estado=desabilitado]:cursor-not-allowed ' +
  'data-[estado=carregando]:cursor-wait';

/**
 * Botão do design system, aplicado como atributo em `<button>` ou `<a>`:
 *
 * ```html
 * <button app-button cor="danger" variante="outline" icone="excluir" (click)="excluir()">Excluir</button>
 * <button app-button variante="ghost" icone="editar" quadrado aria-label="Editar"></button>
 * ```
 *
 * `carregando` mostra spinner, mantém o foco e bloqueia o clique; `desabilitado` usa o `disabled` nativo.
 */
@Component({
  selector: 'button[app-button], a[app-button]',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'classes()',
    '[attr.data-estado]': 'estado()',
    // `disabled` nativo só em desabilitado real: carregando precisa manter o foco.
    '[attr.disabled]': 'desabilitadoNativo() ? "" : null',
    '[attr.aria-disabled]': 'bloqueado() && !desabilitadoNativo() ? "true" : null',
    '[attr.aria-busy]': 'estado() === "carregando" ? "true" : null',
    '[attr.tabindex]': '!ehButton && estado() === "desabilitado" ? "-1" : null',
  },
})
export class ButtonComponent {
  private readonly element: HTMLElement = inject(ElementRef).nativeElement;

  readonly cor = input<ButtonCor>('primary');
  readonly variante = input<ButtonVariante>('solid');
  readonly tamanho = input<ButtonTamanho>('md');
  readonly icone = input<IconeNome>();
  readonly iconePosicao = input<'esquerda' | 'direita'>('esquerda');
  /** Só ícone, sem texto: deixa o botão quadrado. Lembre do `aria-label`. */
  readonly quadrado = input(false, { transform: booleanAttribute });
  readonly carregando = input(false, { transform: booleanAttribute });
  readonly rotuloCarregando = input('Carregando');
  readonly larguraTotal = input(false, { transform: booleanAttribute });
  readonly desabilitado = input(false, { transform: booleanAttribute });

  protected readonly ehButton = this.element.tagName === 'BUTTON';

  protected readonly estado = computed<ButtonEstado>(() =>
    this.carregando() ? 'carregando' : this.desabilitado() ? 'desabilitado' : 'normal',
  );
  protected readonly bloqueado = computed(() => this.estado() !== 'normal');
  protected readonly desabilitadoNativo = computed(() => this.ehButton && this.estado() === 'desabilitado');

  protected readonly classes = computed(() =>
    [
      CLASSES_BASE,
      CLASSES_COR_VARIANTE[this.cor()][this.variante()],
      this.quadrado() ? CLASSES_QUADRADO[this.tamanho()] : CLASSES_TAMANHO[this.tamanho()],
      this.larguraTotal() ? 'w-full' : '',
    ].join(' '),
  );

  // opacity-0 (e não `invisible`) mantém o texto acessível durante o carregamento.
  protected readonly classesConteudo = computed(
    () => `inline-flex items-center gap-[0.25rem] ${this.carregando() ? 'opacity-0' : ''}`,
  );

  protected readonly tamanhoIcone = computed<IconeTamanho>(() => ICONE_TAMANHO[this.tamanho()]);

  constructor() {
    // Fase de captura roda antes dos (click) de quem usa o botão no mesmo elemento.
    const bloquear = (evento: Event) => {
      if (!this.bloqueado()) return;
      evento.preventDefault();
      evento.stopImmediatePropagation();
    };
    this.element.addEventListener('click', bloquear, { capture: true });
    inject(DestroyRef).onDestroy(() => this.element.removeEventListener('click', bloquear, { capture: true }));
  }
}
